<template>
  <div
    ref="popover"
    class="ndsPopover"
  >
    <div
      ref="popoverTrigger"
      class="ndsPopover__trigger"
      data-testid="nds-popover-trigger"
      @click.stop="togglePopover"
    >
      <slot name="trigger" />
    </div>

    <div
      v-show="show"
      ref="popoverContent"
      class="ndsPopover__content"
      data-testid="nds-popover-content"
      @click.stop="handleClickOnPopover"
    >
      <slot name="popover" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, type PropType, nextTick } from 'vue'
import Popper, { type PopperOptions, type Modifiers } from 'popper.js'
import { onClickOutside } from '@vueuse/core'

import { NdsPopoverPlacement } from '@/components/NewDesignSystem/NdsPopover/NdsPopover.interfaces'

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

const props = defineProps({
  closeOnContentClick: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  doTeleport: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String as PropType<NdsPopoverPlacement>,
    default: NdsPopoverPlacement.AUTO,
  },
  modifiers: {
    type: Object as PropType<Modifiers>,
    default: () => {},
  },
})

const show = ref(false)
const popper = ref<Popper>()

const popover = ref<HTMLElement | null>(null)
const popoverTrigger = ref<HTMLElement | null>(null)
const popoverContent = ref<HTMLElement | null>(null)

const options = ref<PopperOptions>({
  positionFixed: true,
  placement: props.placement,
  modifiers: {
    flip: { enabled: true, behavior: 'flip' },
    preventOverflow: {
      enabled: true,
      boundariesElement: 'viewport',
    },
    ...props.modifiers,
  },
})

const moveToBody = () => {
  if (popoverContent.value) {
    document.body.appendChild(popoverContent.value)
  }
}

const moveToContainer = () => {
  if (popoverContent.value && popoverContent.value.parentNode === document.body) {
    popover.value?.appendChild(popoverContent.value)
  }
}

const togglePopover = async () => {
  if (props.disabled) {
    closePopover()
    return
  }

  if (show.value) {
    closePopover()
  } else {
    openPopover()
  }
}

const closePopover = () => {
  show.value = false

  if (popper.value) {
    popper.value.destroy()
  }

  if (props.doTeleport) {
    moveToContainer()
  }

  emit('close')
}

const openPopover = async () => {
  show.value = true

  if (popoverTrigger.value && popoverContent.value) {
    if (props.doTeleport) {
      moveToBody()
    }

    if (props.fullWidth) {
      popoverContent.value.style.width = `${popoverTrigger.value?.offsetWidth}px`
    }

    popper.value = new Popper(popoverTrigger.value, popoverContent.value, options.value)

    // We wait for the nextTick update because popoverContent bounding client values
    // are not available when the Popper instance is created. This causes the Popper
    // content to be misplaced. We update the Popper instance position with
    // popper.value.update() once the popoverContent bounding client values are available.
    await nextTick()
    popper.value.update()
  }

  emit('open')
}

const handleClickOnPopover = () => {
  if (props.closeOnContentClick) {
    closePopover()
  }
}

onClickOutside(popover, () => closePopover(), { ignore: props.doTeleport ? ['.ndsPopover__content'] : [] })

defineExpose({ openPopover, closePopover, show })
</script>

<style lang="scss" scoped>
.ndsPopover {
  &__content {
    z-index: var(--z-index-popover);
  }
}
</style>
